<script lang="ts">
  import { router } from "@inertiajs/svelte"
  import { CheckCircle2, DollarSign } from "@lucide/svelte"

  import { Badge } from "@/components/ui/badge"
  import { Button } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import { Checkbox } from "@/components/ui/checkbox"
  import ConfirmationDialog from "@/components/ui/confirmation-dialog.svelte"
  import AppLayout from "@/layouts/app-layout.svelte"
  import { markPaidPaymentsPath, paymentsPath } from "@/routes"
  import type { BreadcrumbItem, TaskInstance, TaskTemplate } from "@/types"

  interface PaymentSummaryItem {
    user: { id: number; name: string }
    earned: number
    paid: number
    outstanding: number
    unpaidTasks: Array<TaskInstance & { task_template: TaskTemplate }>
  }

  interface Props {
    paymentSummary: PaymentSummaryItem[]
  }

  let { paymentSummary }: Props = $props()

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Payments",
      href: paymentsPath(),
    },
  ]

  let selectedTaskIds = $state<number[]>([])
  let markAllDialogOpen = $state(false)
  let markUserDialogOpen = $state(false)
  let pendingUserId = $state<number | null>(null)

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
  }

  function toggleTask(taskId: number) {
    if (selectedTaskIds.includes(taskId)) {
      selectedTaskIds = selectedTaskIds.filter((id) => id !== taskId)
    } else {
      selectedTaskIds = [...selectedTaskIds, taskId]
    }
  }

  function markAsPaid() {
    if (selectedTaskIds.length === 0) {
      alert("Please select at least one task to mark as paid")
      return
    }

    router.post(
      markPaidPaymentsPath(),
      { task_instance_ids: selectedTaskIds },
      {
        preserveScroll: true,
      },
    )
  }

  function handleMarkAllClick() {
    markAllDialogOpen = true
  }

  function confirmMarkAll() {
    router.post(
      markPaidPaymentsPath(),
      { all: true },
      {
        preserveScroll: true,
      },
    )
  }

  function handleMarkUserClick(userId: number) {
    pendingUserId = userId
    markUserDialogOpen = true
  }

  function confirmMarkUser() {
    if (!pendingUserId) return

    router.post(
      markPaidPaymentsPath(),
      { user_id: pendingUserId },
      {
        preserveScroll: true,
        onSuccess: () => {
          pendingUserId = null
        },
      },
    )
  }

  const totalOutstanding = $derived(
    paymentSummary.reduce((sum, summary) => sum + summary.outstanding, 0),
  )
</script>

<svelte:head>
  <title>Payments - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold">Payments</h1>
        <p class="text-muted-foreground mt-2">Track wages earned and paid</p>
      </div>
      <div class="flex items-center gap-2">
        {#if totalOutstanding > 0}
          <Button onclick={handleMarkAllClick} variant="default">
            <CheckCircle2 class="h-4 w-4" />
            Mark All Outstanding Paid
          </Button>
        {/if}
        {#if selectedTaskIds.length > 0}
          <Button onclick={markAsPaid}>
            <CheckCircle2 class="h-4 w-4" />
            Mark {selectedTaskIds.length} as Paid
          </Button>
        {/if}
      </div>
    </div>

    <div class="space-y-6">
      {#each paymentSummary as summary (summary.user.id)}
        <Card>
          <CardHeader>
            <div class="flex items-center justify-between">
              <div>
                <CardTitle>{summary.user.name}</CardTitle>
                <CardDescription>Payment summary</CardDescription>
              </div>
              {#if summary.outstanding > 0}
                <Button
                  variant="default"
                  onclick={() => handleMarkUserClick(summary.user.id)}
                >
                  {formatCurrency(summary.outstanding)} outstanding
                </Button>
              {:else}
                <Badge variant="secondary">
                  {formatCurrency(summary.outstanding)} outstanding
                </Badge>
              {/if}
            </div>
          </CardHeader>
          <CardContent class="space-y-4">
            <div class="grid gap-4 md:grid-cols-3">
              <div class="flex items-center gap-2">
                <DollarSign class="text-muted-foreground h-5 w-5" />
                <div>
                  <p class="text-muted-foreground text-sm">Total Earned</p>
                  <p class="text-lg font-semibold">
                    {formatCurrency(summary.earned)}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <DollarSign class="text-muted-foreground h-5 w-5" />
                <div>
                  <p class="text-muted-foreground text-sm">Total Paid</p>
                  <p class="text-lg font-semibold">
                    {formatCurrency(summary.paid)}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <DollarSign class="text-muted-foreground h-5 w-5" />
                <div>
                  <p class="text-muted-foreground text-sm">Outstanding</p>
                  <p class="text-lg font-semibold">
                    {formatCurrency(summary.outstanding)}
                  </p>
                </div>
              </div>
            </div>

            {#if summary.unpaidTasks.length > 0}
              <div class="space-y-1">
                <h3 class="font-medium">Unpaid Tasks</h3>
                <div class="space-y-1">
                  {#each summary.unpaidTasks as task (task.id)}
                    <div class="flex items-center gap-3 rounded-lg border p-1">
                      <Checkbox
                        checked={selectedTaskIds.includes(task.id)}
                        onCheckedChange={() => toggleTask(task.id)}
                      />
                      <div class="flex-1">
                        <p class="font-medium">
                          {task.task_template.name}
                          {#if task.completed_at}
                            <span class="text-muted-foreground text-xs">
                              {` (${formatDate(task.completed_at)})`}
                            </span>
                          {/if}
                        </p>
                      </div>
                      <div class="text-right">
                        <p class="font-semibold">
                          {formatCurrency(
                            Number.parseFloat(task.task_template.wage),
                          )}
                        </p>
                      </div>
                    </div>
                  {/each}
                </div>
              </div>
            {/if}
          </CardContent>
        </Card>
      {/each}
    </div>
  </div>

  <ConfirmationDialog
    bind:open={markAllDialogOpen}
    title="Mark All Outstanding Tasks Paid"
    description="Are you sure you want to mark all outstanding tasks as paid? This action cannot be undone."
    onConfirm={confirmMarkAll}
  />

  <ConfirmationDialog
    bind:open={markUserDialogOpen}
    title="Mark User Tasks Paid"
    description="Are you sure you want to mark all outstanding tasks for this user as paid? This action cannot be undone."
    onConfirm={confirmMarkUser}
  />
</AppLayout>
